import type { EventMarketDto } from '@/api/queries';
import {
  calculateTicket,
  getBetOddFactor,
  type BetslipBet,
  type BetslipEvent,
  type BetslipItem,
  type BetslipSystemLimits,
  type BetslipTicket,
} from './betslip';
import { readOutcomeOdd } from './live-odds';
import {
  applyLiveStatisticsPatch,
  resolveEventRowLiveInfo,
  type LiveEventStatisticsPatch,
} from './live-statistics';

export type LiveEventMetaPatch = {
  active?: boolean;
  marketsCount?: number;
  expireOdds?: string;
};

export type BetslipLiveFeedGetters = {
  getOddsPatch: (eventId: number) => EventMarketDto[] | undefined;
  getStatisticsPatch: (eventId: number) => LiveEventStatisticsPatch | undefined;
  getEventMetaPatch: (eventId: number) => LiveEventMetaPatch | undefined;
};

function toEventIdNumber(eventId: string | number): number | null {
  const value = typeof eventId === 'number' ? eventId : Number(eventId);
  return Number.isFinite(value) ? value : null;
}

function isEventLiveFlag(live: BetslipEvent['Live']): boolean {
  return live === true || live === 'true' || live === '1';
}

function scoreString(value: string | number | undefined | null): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function sameOdd(a: BetslipBet['Odd'], b: number): boolean {
  const left = typeof a === 'number' ? a : Number.parseFloat(String(a ?? ''));
  if (!Number.isFinite(left) || !Number.isFinite(b)) return false;
  return Number(left.toFixed(2)) === Number(b.toFixed(2));
}

function enrichBet(
  bet: BetslipBet,
  markets: EventMarketDto[] | undefined,
  eventActive: boolean,
): { bet: BetslipBet; oddChanged: boolean; touched: boolean } {
  if (!markets?.length || !bet.MarketId || !bet.Outcome) {
    return { bet, oddChanged: false, touched: false };
  }

  const odd = readOutcomeOdd(markets, bet.MarketId, bet.Specifier ?? undefined, bet.Outcome, {
    exactOutcome: true,
  });
  if (!odd) {
    return { bet, oddChanged: false, touched: false };
  }

  const blocked =
    !eventActive || !odd.marketActive || !odd.lineActive || !odd.active || odd.value <= 0;
  const oddChanged = !sameOdd(bet.Odd, odd.value);
  const blockedChanged = Boolean(bet.Blocked) !== blocked;

  if (!oddChanged && !blockedChanged) {
    return { bet, oddChanged: false, touched: false };
  }

  return {
    bet: {
      ...bet,
      Odd: odd.value,
      Blocked: blocked,
    },
    oddChanged,
    touched: true,
  };
}

function enrichEventFromStatistics(
  event: BetslipEvent,
  statsPatch: LiveEventStatisticsPatch | undefined,
): { event: BetslipEvent; touched: boolean } {
  if (!statsPatch) {
    return { event, touched: false };
  }

  const patched = applyLiveStatisticsPatch(
    {
      HomeScore:
        event.HomeScore !== undefined ? Number(event.HomeScore) : undefined,
      AwayScore:
        event.AwayScore !== undefined ? Number(event.AwayScore) : undefined,
      Time: event.Time ?? null,
      Period: event.Period ?? null,
      PeriodShort: event.PeriodShort ?? null,
      Statistics: event.TimeExtended
        ? JSON.stringify({
            CurrentTimeExtended: event.TimeExtended,
            MatchTimeExtended: event.TimeExtended,
          })
        : null,
    },
    statsPatch,
  );

  const liveInfo = resolveEventRowLiveInfo(patched);
  const homeScore = liveInfo.homeScore;
  const awayScore = liveInfo.awayScore;
  const score =
    homeScore !== undefined && awayScore !== undefined
      ? `${homeScore}:${awayScore}`
      : event.Score;

  // Persist raw period/time codes so the UI can translate period labels on render.
  const next: BetslipEvent = {
    ...event,
    Live: true,
    HomeScore: homeScore ?? event.HomeScore,
    AwayScore: awayScore ?? event.AwayScore,
    Score: score,
    Time: statsPatch.currentTime ?? event.Time,
    Period: statsPatch.currentPart ?? patched.Period ?? event.Period,
    PeriodShort:
      statsPatch.currentPartShort ?? patched.PeriodShort ?? event.PeriodShort,
    TimeExtended: statsPatch.currentTimeExtended ?? event.TimeExtended,
  };

  const touched =
    !isEventLiveFlag(event.Live) ||
    scoreString(event.HomeScore) !== scoreString(next.HomeScore) ||
    scoreString(event.AwayScore) !== scoreString(next.AwayScore) ||
    (event.Time ?? '') !== (next.Time ?? '') ||
    (event.Period ?? '') !== (next.Period ?? '') ||
    (event.PeriodShort ?? '') !== (next.PeriodShort ?? '') ||
    (event.TimeExtended ?? '') !== (next.TimeExtended ?? '') ||
    (event.Score ?? '') !== (next.Score ?? '');

  return { event: next, touched };
}

function enrichItem(
  item: BetslipItem,
  getters: BetslipLiveFeedGetters,
): { item: BetslipItem; oddChanged: boolean; touched: boolean } {
  const eventId = toEventIdNumber(item.Event.EventId);
  if (eventId == null) {
    return { item, oddChanged: false, touched: false };
  }

  const oddsPatch = getters.getOddsPatch(eventId);
  const statsPatch = getters.getStatisticsPatch(eventId);
  const metaPatch = getters.getEventMetaPatch(eventId);
  const eventActive = metaPatch?.active !== false;

  let oddChanged = false;
  let touched = false;

  const bets = item.Bets.map((bet) => {
    const result = enrichBet(bet, oddsPatch, eventActive);
    oddChanged = oddChanged || result.oddChanged;
    touched = touched || result.touched;
    return result.bet;
  });

  // Event inactive with known meta → block every bet even without per-outcome patch.
  const betsWithMeta =
    metaPatch?.active === false
      ? bets.map((bet) => {
          if (bet.Blocked) return bet;
          touched = true;
          return { ...bet, Blocked: true };
        })
      : bets;

  const statsResult = enrichEventFromStatistics(item.Event, statsPatch);
  touched = touched || statsResult.touched;

  // Live meta/odds without stats still mark the leg live when already flagged or patching.
  let event = statsResult.event;
  if (!isEventLiveFlag(event.Live) && (statsPatch || oddsPatch)) {
    // Only promote to live when statistics prove in-play, or the selection was already live.
    if (statsPatch) {
      event = { ...event, Live: true };
      touched = true;
    }
  }

  if (!touched) {
    return { item, oddChanged: false, touched: false };
  }

  return {
    item: { Event: event, Bets: betsWithMeta },
    oddChanged,
    touched: true,
  };
}

/**
 * Merge live feed odds/statistics/meta into a betslip ticket.
 * Returns the same ticket reference when nothing material changed.
 */
export function enrichBetslipTicketWithLiveFeed(
  ticket: BetslipTicket,
  getters: BetslipLiveFeedGetters,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  if (!ticket.Items.length) return ticket;

  let anyTouched = false;
  let anyOddChanged = false;

  const items = ticket.Items.map((item) => {
    const result = enrichItem(item, getters);
    anyTouched = anyTouched || result.touched;
    anyOddChanged = anyOddChanged || result.oddChanged;
    return result.item;
  });

  if (!anyTouched) return ticket;

  const next: BetslipTicket = {
    ...ticket,
    Items: items,
    Changed: ticket.Changed || anyOddChanged,
  };

  return calculateTicket(next, limits);
}

/** True when any selection is currently blocked / closed. */
export function betslipHasBlockedSelection(ticket: BetslipTicket): boolean {
  return ticket.Items.some((item) => item.Bets.some((bet) => Boolean(bet.Blocked)));
}

/** True when the ticket has at least one live event. */
export function betslipHasLiveSelection(ticket: BetslipTicket): boolean {
  return ticket.Items.some((item) => isEventLiveFlag(item.Event.Live));
}

/** Snapshot of material live fields used to skip redundant store writes. */
export function betslipLiveMaterialSignature(ticket: BetslipTicket): string {
  const parts: string[] = [];
  for (const item of ticket.Items) {
    const event = item.Event;
    parts.push(
      `${event.EventId}|${String(event.Live)}|${event.HomeScore ?? ''}|${event.AwayScore ?? ''}|${event.Time ?? ''}|${event.Period ?? ''}|${event.PeriodShort ?? ''}|${event.TimeExtended ?? ''}`,
    );
    for (const bet of item.Bets) {
      parts.push(`${getBetOddFactor(bet)}|${bet.Blocked ? 1 : 0}`);
    }
  }
  return parts.join(';');
}
